import { Center, IconButton, InputLeftElement } from '@chakra-ui/react';
import { ReactComponent as SearchIcon } from 'assets/icons/search.svg';
import { useCallback } from 'react';

import { AutoCompleteInput, AutoCompleteInputItem, AutoCompleteInputList } from '../AutoCompleteInput';
import { AutoCompleteClearButton } from '../AutoCompleteInput/AutoCompleteClearButton';
import { AutoCompleteInputGroup } from '../AutoCompleteInput/AutoCompleteInput';

/**
 * AUTOCOMPLETE SEARCH INPUT COMPONENT
 *
 * Renders a search input with autocomplete suggestions as the user types.
 * - Includes clear input button and search icon.
 * - Includes a11y support for autocomplete suggestions.
 */
interface AutoCompleteSearchInputProps {
  onSearch: (query: string) => void;
  autoCompleteList: string[];

  defaultValue?: string;
  placeholder?: string;
  label?: string;
}

export const AutoCompleteSearchInput = ({
  onSearch,
  autoCompleteList,
  placeholder,
  label,
  defaultValue,
}: AutoCompleteSearchInputProps) => {
  const handleSelectSearchSuggestion = useCallback(
    (selectedIds: string[]) => {
      if (selectedIds.length === 0) return;
      onSearch(selectedIds[0]);
    },
    [onSearch],
  );

  return (
    <AutoCompleteInput
      placeholder={placeholder}
      onChange={handleSelectSearchSuggestion}
      label={label}
      defaultValue={defaultValue}
      openListOnFocus
      submitValueOnEnter
      focusOnMenuOpen
    >
      <AutoCompleteInputGroup>
        <InputLeftElement h={'full'} w={'fit-content'}>
          <IconButton
            size='xs'
            borderRadius='full'
            variant='unstyled'
            aria-label={label ? label : ''}
            icon={
              <Center>
                <SearchIcon stroke='var(--vg-colors-upgrade-blue-500' />
              </Center>
            }
            w={10}
          />
        </InputLeftElement>
        <AutoCompleteClearButton />
      </AutoCompleteInputGroup>
      <AutoCompleteInputList>
        {autoCompleteList.map((suggestion) => (
          <AutoCompleteInputItem key={suggestion} value={suggestion} name={suggestion}>
            {suggestion}
          </AutoCompleteInputItem>
        ))}
      </AutoCompleteInputList>
    </AutoCompleteInput>
  );
};
